import {
  CacheGroupedItemsGroupByEnum,
  FetchCacheGroupedItemsFilters,
  FetchGroupedItemsCacheKey,
  FetchGroupedItemsGqlQuery,
  FetchGroupedItemsLimit,
  FetchGroupedItemsSort,
  FetchGroupedItemsSortType
} from '../../groupedItemsTypes';
import {
  FetchCacheItemSources,
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchCacheItemsTrackWithMetadata,
  FetchItemCacheKey,
  FetchItemGqlQuery,
  FetchItemsGqlQuery
} from '../../../../types';
import { ReadInfiniteIndexQueryBaseNodeType } from '../../../common/reactQuery/hooks/useReadInfiniteIndexQuery';

import { useCacheInfiniteIndexQuery } from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  GroupedItemsPermissions,
  INITIAL_CACHE_GROUPED_ITEMS_FILTERS,
  INITIAL_CACHE_GROUPED_ITEMS_LIMIT,
  INITIAL_CACHE_GROUPED_ITEMS_SORT
} from '../../groupedItemsConstants';

interface CacheGroupedItemsWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
  fetchCacheItemQuery?: FetchItemGqlQuery;
  fetchCacheItemSource?: FetchCacheItemSources;
}

interface CacheGroupedItemsWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
  fetchCacheItemQuery?: never;
  fetchCacheItemSource?: never;
}

interface CacheGroupedItemsDefaultOptions {
  additionalParams?: {
    groupBy?: CacheGroupedItemsGroupByEnum[];
    groupItemsByType?: boolean;
    convertTo?: string;
    currencyRateId?: string;
    showTotal?: boolean;
    sort?: FetchGroupedItemsSortType;
  };
  cacheKey: FetchGroupedItemsCacheKey;
  query: FetchGroupedItemsGqlQuery;
  cacheQuery?: FetchGroupedItemsGqlQuery;
  initialFilters?: FetchCacheGroupedItemsFilters;
  initialSort?: FetchGroupedItemsSort;
  initialLimit?: FetchGroupedItemsLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  withMetadata?: FetchCacheItemsTrackWithMetadata;
}

type CacheGroupedItemsOptions = CacheGroupedItemsDefaultOptions &
  (
    | CacheGroupedItemsWithPrefetchItemOptions
    | CacheGroupedItemsWithoutPrefetchItemOptions
  );

const scope = 'groupedItems';

function useCacheGroupedItems<
  GroupedItemItemType extends ReadInfiniteIndexQueryBaseNodeType,
  GroupedItemMetaType = undefined
>({
  additionalParams,
  cacheKey,
  cacheQuery,
  fetchCacheItemQuery,
  fetchCacheItemSource = FetchCacheItemSources.MONGO,
  fetchItemCacheKey,
  fetchItemQuery,
  initialFilters = INITIAL_CACHE_GROUPED_ITEMS_FILTERS,
  initialLimit = INITIAL_CACHE_GROUPED_ITEMS_LIMIT,
  initialSort = INITIAL_CACHE_GROUPED_ITEMS_SORT,
  options = {},
  query,
  source = FetchCacheItemsSources.MONGO,
  trackTotalHits = false,
  withMetadata = false
}: CacheGroupedItemsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    hasNextPage,
    updateItemCache,
    // updateItemsCache,
    loadMoreItems,
    filterItems,
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    sortItems,
    limitItems,
    meta,
    dataUpdatedAt
  } = useCacheInfiniteIndexQuery<GroupedItemItemType, GroupedItemMetaType>({
    action: GroupedItemsPermissions.READ_GROUPED_ITEMS_INDEX_MONGO_QUERY,
    additionalParams,
    cacheKey,
    cacheQuery,
    defaultApi: 'cache_api',
    fetchCacheItemQuery,
    fetchCacheItemSource,
    fetchItemCacheKey,
    fetchItemQuery,
    initialFilters,
    initialLimit,
    initialSort,
    options,
    query,
    scope,
    source,
    trackTotalHits,
    withMetadata
  });

  return {
    groupedItemsData: data,
    groupedItems: items,
    groupedItemsError: itemsError,
    groupedItemsTotalCount: itemsTotalCount,
    groupedItemsFetched: isFetched,
    groupedItemsLoading: isLoading,
    groupedItemsFetchingNextPage: isFetchingNextPage,
    groupedItemsIsPlaceholderData: isPlaceholderData,
    groupedItemsFilters: currentFilters,
    groupedItemsSort: currentSort,
    groupedItemsPage: currentPage,
    groupedItemsLimit: currentLimit,
    groupedItemsHasNextPage: hasNextPage,
    updateGroupedItemCache: updateItemCache,
    loadMoreGroupedItems: loadMoreItems,
    filterGroupedItems: filterItems,
    changeGroupedItemsFilters: changeItemsFilters,
    clearGroupedItemsFilters: clearItemsFilters,
    clearGroupedItemsFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortGroupedItems: sortItems,
    limitGroupedItems: limitItems,
    groupedItemsMeta: meta,
    groupedItemsDataUpdatedAt: dataUpdatedAt
    // prefetchGroupedItem: useCallback(
    //   (groupedItemNanoId: GroupedItemNanoID) => {
    //     const nanoId = groupedItemNanoId as unknown as UUID;
    //     prefetchItem(nanoId);
    //   },
    //   [prefetchItem]
    // )
  };
}

export default useCacheGroupedItems;
